export class SeededRng {
    private state: number;

    constructor(seed: string) {
        this.state = SeededRng.hash32(seed);
    }

    random(): number {
        // Mulberry32
        this.state = (this.state + 0x6d2b79f5) | 0;
        let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    randomInt(min: number, max: number): number {
        return Math.floor(this.random() * (max - min)) + min;
    }

    /**
     * One of `items`, each equally likely: one draw, the same as
     * `items[randomInt(0, items.length)]`. Undefined when `items` is empty
     * (the draw is still taken, so the stream stays in step).
     */
    pick<T>(items: readonly T[]): T | undefined {
        return items[this.randomInt(0, items.length)];
    }

    shuffle<T>(array: T[]): T[] {
        const result = [...array];
        for (let i = result.length - 1; i > 0; i -= 1) {
            const j = this.randomInt(0, i + 1);
            [result[i], result[j]] = [result[j]!, result[i]!];
        }
        return result;
    }

    /**
     * FNV-1a hash of an arbitrary string into a uint32. Used to seed a
     * SeededRng's internal state, and available statically for callers that
     * need a deterministic hash (e.g. deriving sub-seeds, or a probability
     * roll) without needing an RNG's stream of successive values.
     */
    static hash32(input: string): number {
        let hash = 2166136261;
        for (let i = 0; i < input.length; i += 1) {
            hash ^= input.charCodeAt(i);
            hash = Math.imul(hash, 16777619);
        }
        return hash >>> 0;
    }

    /**
     * Derives a deterministic sub-seed string from this hash for a named
     * domain, e.g. `SeededRng.deriveSeed(rootSeed, 'layout')`. Useful for
     * splitting one root seed into independent seeds per generation phase.
     */
    static deriveSeed(rootSeed: string, domain: string): string {
        return `s-${SeededRng.hash32(`${rootSeed}|${domain}`).toString(16).padStart(8, '0')}`;
    }
}

/**
 * Rolls one of `entries`, each as likely as its weight (`weightOf`) out of
 * all their weights: one draw from `rng`. An entry without weight (0 or
 * less) is never picked; undefined when none has any.
 */
export function rollWeighted<T>(entries: readonly T[], weightOf: (entry: T) => number, rng: { random(): number }): T | undefined {
    const weighted = entries.filter((entry) => weightOf(entry) > 0);
    const total = weighted.reduce((sum, entry) => sum + weightOf(entry), 0);
    if (total <= 0) {
        return undefined;
    }
    let cursor = rng.random() * total;
    for (const entry of weighted) {
        cursor -= weightOf(entry);
        if (cursor < 0) {
            return entry;
        }
    }
    return weighted[weighted.length - 1];
}
